import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import type { PendingIntakeEntry } from '@/hooks/useEntryIntake'
import { canSyncPendingEntry, syncsWithAll } from '@/hooks/pending-entry-sync'

type Props = {
  entries: PendingIntakeEntry[]
  showAllowsIntake: boolean
  syncDisabled: boolean
  syncDisabledReason?: string
  canReplaceCode: boolean
  onSyncAll: () => void
  onSync: (entry: PendingIntakeEntry) => void
  onRemove: (entry: PendingIntakeEntry) => void
  onReplace: (entry: PendingIntakeEntry) => void
  onReview: (id: string) => void
  onSaveNotes: (entry: PendingIntakeEntry, value: string) => Promise<boolean>
}

const STATUS_BADGES: Record<
  PendingIntakeEntry['status'],
  { label: string; text?: string; variant?: 'outline' | 'destructive'; className?: string }
> = {
  queued: { label: 'Queued', variant: 'outline' },
  syncing: { label: 'Syncing', text: 'Syncing…' },
  error: { label: 'Failed', variant: 'destructive' },
  duplicate: {
    label: 'Already entered',
    className: 'border-amber-400 bg-amber-50 text-amber-900',
  },
}

const controlId = (action: string, id: string) => `pending-${action}-${id}`

export function PendingEntriesSection({
  entries,
  showAllowsIntake,
  syncDisabled,
  syncDisabledReason,
  canReplaceCode,
  onSyncAll,
  onSync,
  onRemove,
  onReplace,
  onReview,
  onSaveNotes,
}: Props) {
  const [editingSnapshot, setEditingSnapshot] = useState<PendingIntakeEntry | null>(null)
  const [draftNotes, setDraftNotes] = useState('')
  const [savingNotes, setSavingNotes] = useState(false)
  // A status change can move a row to another group, which replaces its
  // buttons. Controls are found again by id so focus can follow the row.
  const notesTrigger = useRef<string | null>(null)
  const actionControl = useRef<HTMLElement | null>(null)
  const queuedEditingEntry = entries.find(entry => entry.id === editingSnapshot?.id)
  const editingEntry = queuedEditingEntry ?? editingSnapshot
  const savedNotes = queuedEditingEntry?.notes ?? ''
  const notesChangedElsewhere =
    Boolean(queuedEditingEntry) &&
    savedNotes !== (editingSnapshot?.notes ?? '') &&
    savedNotes !== draftNotes
  const groups = useMemo(() => {
    const grouped = new Map<string, PendingIntakeEntry[]>()
    for (const entry of entries) {
      const key = JSON.stringify([entry.exhibitor.id, entry.status, entry.error ?? null])
      const group = grouped.get(key)
      if (group) group.push(entry)
      else grouped.set(key, [entry])
    }
    return [...grouped.values()].map(items => ({
      entries: items,
      sharedError: items.length > 1 && items[0].status === 'error' ? items[0].error : undefined,
    }))
  }, [entries])

  useLayoutEffect(() => {
    let control = actionControl.current
    if (!control) return
    if (!control.isConnected) control = actionControl.current = document.getElementById(control.id)
    // Focus falls to the body when its control is replaced or disabled mid-sync.
    const active = document.activeElement
    if (!active || active === document.body) control?.focus({ preventScroll: true })
    else if (active !== control) actionControl.current = null
  }, [entries])

  const saveNotes = async () => {
    if (
      !queuedEditingEntry ||
      notesChangedElsewhere ||
      queuedEditingEntry.status === 'syncing' ||
      savingNotes
    )
      return
    // Any notes write counts as an edit and sends a submitted entry to review.
    if (draftNotes === savedNotes) {
      setEditingSnapshot(null)
      return
    }
    setSavingNotes(true)
    try {
      if (await onSaveNotes(queuedEditingEntry, draftNotes)) setEditingSnapshot(null)
    } finally {
      setSavingNotes(false)
    }
  }

  return (
    <section
      aria-labelledby="pending-entries-heading"
      className="@container/pending-entries min-w-0 space-y-5"
      onClickCapture={event => {
        actionControl.current = (event.target as HTMLElement).closest<HTMLElement>('li button[id]')
      }}
      onBlur={event => {
        // Some browsers also blur a control as it is removed; only a control
        // that is still mounted afterwards was left on purpose.
        const blurred = event.target
        queueMicrotask(() => {
          if (blurred === actionControl.current && blurred.isConnected) actionControl.current = null
        })
      }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h3 id="pending-entries-heading" className="text-xl font-semibold">
            Pending Entries <span className="text-muted-foreground">({entries.length})</span>
          </h3>
          <p className="text-sm text-muted-foreground">Entries waiting for sync on this device.</p>
        </div>
        <Button
          className="min-h-11 w-full @sm/pending-entries:w-auto"
          disabled={syncDisabled || !entries.some(entry => syncsWithAll(entry, showAllowsIntake))}
          title={syncDisabledReason}
          onClick={onSyncAll}
        >
          Sync pending ({entries.length})
        </Button>
      </div>
      {!showAllowsIntake && (
        <p className="text-sm text-muted-foreground">
          Intake is closed. Only previously submitted work can be verified; new entries and
          check-ins stay blocked.
        </p>
      )}
      {entries.length === 0 && <p className="text-sm text-muted-foreground">No pending entries.</p>}
      {groups.map((group, groupIndex) => {
        const first = group.entries[0]
        const badge = STATUS_BADGES[first.status]
        const messageId = `pending-group-error-${groupIndex}`
        // Keyed by its first entry so a status change does not remount the
        // whole group.
        return (
          <section
            key={first.id}
            aria-label={`Pending entries for ${first.exhibitor.name}, ${badge.label}, group ${groupIndex + 1}`}
            className="min-w-0 space-y-3"
          >
            {group.sharedError && (
              <p
                id={messageId}
                className="rounded-md bg-muted p-3 text-sm leading-relaxed break-words"
              >
                {group.sharedError}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="min-w-0 max-w-full font-semibold break-words">
                {first.exhibitor.name}
              </h4>
              <span className="text-sm text-muted-foreground">
                {group.entries.length} {group.entries.length === 1 ? 'entry' : 'entries'}
              </span>
              <Badge variant={badge.variant} className={badge.className}>
                {badge.text ?? badge.label}
              </Badge>
            </div>
            <ul
              aria-label={`Entries for ${first.exhibitor.name}, ${badge.label}, group ${groupIndex + 1}`}
              className="divide-y border-y"
            >
              {group.entries.map(entry => {
                const removalBlocked = Boolean(entry.syncAttempt && !entry.syncAttempt.completed)
                const savedAttemptEntryId = entry.syncAttempt?.createdEntry?.id
                // Until an attempt completes, existingEntryId names the earlier
                // entry a duplicate rejection reported, not a saved copy of this one.
                const savedEntryId =
                  savedAttemptEntryId ??
                  (entry.syncAttempt?.completed ? entry.existingEntryId : undefined)
                const reviewEntryId = savedEntryId ?? entry.existingEntryId
                const removalReason = removalBlocked
                  ? savedAttemptEntryId
                    ? 'This entry was saved. Use Sync now to finish or verify the remaining work.'
                    : 'The save outcome is unknown. Use Sync now when connected to verify it before removing.'
                  : undefined
                return (
                  <li
                    key={entry.id}
                    data-testid={`pending-entry-${entry.id}`}
                    aria-describedby={group.sharedError ? messageId : undefined}
                    className="min-w-0 py-4 @2xl/pending-entries:grid @2xl/pending-entries:grid-cols-[minmax(0,1fr)_auto] @2xl/pending-entries:gap-x-5"
                  >
                    <div className="min-w-0 space-y-2">
                      <div className="flex items-baseline gap-3">
                        <span className="shrink-0 font-mono text-base font-semibold">
                          <span className="sr-only">Exhibit ID </span>
                          {entry.code}
                        </span>
                        <p className="min-w-0 text-sm font-medium break-words">{entry.className}</p>
                      </div>
                      {entry.notes && (
                        <p className="line-clamp-2 break-words text-sm text-muted-foreground">
                          {entry.notes}
                        </p>
                      )}
                      {entry.error && !group.sharedError && (
                        <p className="text-sm text-muted-foreground break-words">{entry.error}</p>
                      )}
                      {entry.status === 'duplicate' && (
                        <p className="text-sm text-muted-foreground break-words">
                          Remove this entry, or mark the other entry Not Entered and sync again.
                        </p>
                      )}
                      {removalReason && (
                        <p
                          id={`pending-removal-${entry.id}`}
                          className="text-sm text-muted-foreground break-words"
                        >
                          {removalReason}
                        </p>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap items-start gap-2 @2xl/pending-entries:mt-0 @2xl/pending-entries:max-w-[320px] @2xl/pending-entries:justify-end">
                      <Button
                        id={controlId('sync', entry.id)}
                        variant="outline"
                        className="min-h-11"
                        disabled={syncDisabled || !canSyncPendingEntry(entry, showAllowsIntake)}
                        title={syncDisabledReason}
                        onClick={() => onSync(entry)}
                      >
                        Sync now
                      </Button>
                      <Button
                        id={controlId('remove', entry.id)}
                        variant="outline"
                        className="min-h-11"
                        disabled={removalBlocked}
                        title={removalReason}
                        aria-describedby={
                          removalBlocked ? `pending-removal-${entry.id}` : undefined
                        }
                        onClick={() => onRemove(entry)}
                      >
                        Remove
                      </Button>
                      <Button
                        id={controlId('notes', entry.id)}
                        variant="ghost"
                        className="min-h-11"
                        disabled={entry.status === 'syncing'}
                        aria-label={`${entry.notes ? 'Edit' : 'Add'} notes for Exhibit ID ${entry.code}`}
                        onClick={() => {
                          notesTrigger.current = entry.id
                          setDraftNotes(entry.notes ?? '')
                          setEditingSnapshot(entry)
                        }}
                      >
                        {entry.notes ? 'Edit notes' : 'Add notes'}
                      </Button>
                      {entry.canReplaceCode && canReplaceCode && (
                        <Button
                          id={controlId('replace', entry.id)}
                          variant="outline"
                          className="h-auto min-h-11 max-w-full whitespace-normal text-left"
                          onClick={() => onReplace(entry)}
                        >
                          Replace rejected Exhibit ID
                        </Button>
                      )}
                      {reviewEntryId && (
                        <Button
                          id={controlId('review', entry.id)}
                          variant="outline"
                          className="h-auto min-h-11 max-w-full whitespace-normal text-left"
                          onClick={() => onReview(reviewEntryId)}
                        >
                          {savedEntryId ? 'Review saved entry' : 'Review existing entry'}
                        </Button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          </section>
        )
      })}
      <Dialog
        open={Boolean(editingEntry)}
        onOpenChange={open => !open && !savingNotes && setEditingSnapshot(null)}
      >
        <DialogContent
          className="items-end p-0 sm:items-stretch sm:justify-items-end"
          contentClassName="max-h-[85dvh] overflow-y-auto max-w-none rounded-b-none sm:max-h-full sm:max-w-sm sm:rounded-none flex flex-col gap-5"
          onCloseAutoFocus={event => {
            event.preventDefault()
            if (notesTrigger.current)
              document.getElementById(controlId('notes', notesTrigger.current))?.focus()
          }}
        >
          <DialogHeader className="text-left">
            <DialogTitle>Notes · Exhibit ID {editingEntry?.code}</DialogTitle>
            <DialogDescription>{editingEntry?.className}</DialogDescription>
          </DialogHeader>
          {editingEntry && !queuedEditingEntry && (
            <p role="alert" className="text-sm text-muted-foreground">
              This entry left the pending queue while you were editing. Your draft has not been
              saved. Copy it before closing.
            </p>
          )}
          {notesChangedElsewhere && (
            <p role="alert" className="text-sm text-muted-foreground">
              These notes were changed in another tab while you were editing. Your draft has not
              been saved. Copy it, then close and reopen this editor to see the latest notes.
            </p>
          )}
          <div className="space-y-2">
            <label htmlFor="pending-entry-notes" className="text-sm font-medium">
              Notes
            </label>
            <Textarea
              id="pending-entry-notes"
              value={draftNotes}
              onChange={event => setDraftNotes(event.target.value)}
              disabled={savingNotes || editingEntry?.status === 'syncing'}
              rows={5}
              placeholder="Add notes for this entry…"
              className="min-h-32 resize-y"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {queuedEditingEntry?.syncAttempt
              ? 'Saved on this device. This entry was already submitted without these notes, so it will stop for review after it syncs.'
              : 'Saved on this device and included when this entry syncs.'}
          </p>
          <DialogFooter>
            <Button
              className="min-h-11"
              variant="outline"
              disabled={savingNotes}
              onClick={() => setEditingSnapshot(null)}
            >
              Cancel
            </Button>
            <Button
              className="min-h-11"
              disabled={
                savingNotes ||
                !queuedEditingEntry ||
                notesChangedElsewhere ||
                queuedEditingEntry.status === 'syncing'
              }
              onClick={() => void saveNotes()}
            >
              {savingNotes ? 'Saving…' : 'Save notes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
